/**
 * MainScene — Three.js scene initialization (T-1.1.3 + T-1.1.4 grid floor).
 *
 * Owns:
 *   - WebGLRenderer (alpha:true, antialias:true)
 *   - Scene (singleton)
 *   - PerspectiveCamera (fov=50, near=0.1, far=2000, position (10,10,10))
 *   - OrbitControls (damping 0.05, zoom/pan/rotate 全开)
 *   - requestAnimationFrame loop (with cancellable handle)
 *   - GridHelper (T-1.1.4, default 16×16 at y=0, lazily attached)
 *
 * Constitution §2.3 #3 — single renderer, single scene. Do not add a second
 * 3D pipeline here. Six-direction views (M3) use a 2D Canvas projection, not
 * a second WebGL scene.
 *
 * Lifecycle: caller drives mount/unmount. React `MainCanvas` wraps this in a
 * useEffect; dispose() must be safe to call twice.
 */
import { WebGLRenderer, Scene, PerspectiveCamera, GridHelper } from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import {
  createGridFloor,
  GRID_FLOOR_DEFAULT_DIVISIONS,
  GRID_FLOOR_DEFAULT_SIZE,
} from './GridFloor';

const CAMERA_FOV = 50;
const CAMERA_NEAR = 0.1;
const CAMERA_FAR = 2000;
const CAMERA_POSITION: readonly [number, number, number] = [10, 10, 10];
const SCENE_CENTER: readonly [number, number, number] = [0, 0, 0];
const CONTROLS_DAMPING = 0.05;

export class MainScene {
  private readonly canvas: HTMLCanvasElement;
  private readonly renderer: WebGLRenderer;
  private readonly scene: Scene;
  private readonly camera: PerspectiveCamera;
  private controls: OrbitControls | null = null;
  private rafHandle: number | null = null;
  private disposed = false;
  private gridFloor: GridHelper | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;

    // Renderer — alpha for transparent canvas over page background, antialias
    // for clean voxel edges in later milestones.
    this.renderer = new WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true,
    });

    // Scene singleton.
    this.scene = new Scene();

    // Camera — fov 50° is a reasonable 3D-modeling default; near 0.1 / far
    // 2000 supports both close voxel work and far orbit.
    this.camera = new PerspectiveCamera(CAMERA_FOV, 1, CAMERA_NEAR, CAMERA_FAR);
    this.camera.position.set(CAMERA_POSITION[0], CAMERA_POSITION[1], CAMERA_POSITION[2]);
    this.camera.lookAt(SCENE_CENTER[0], SCENE_CENTER[1], SCENE_CENTER[2]);

    // The renderer owns this canvas — set pixel ratio early for crisp output
    // on retina displays.
    this.renderer.setPixelRatio(globalThis.devicePixelRatio ?? 1);
  }

  // ---------- getters (read-only singletons) ----------

  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  getCamera(): PerspectiveCamera {
    return this.camera;
  }

  getScene(): Scene {
    return this.scene;
  }

  getRenderer(): WebGLRenderer {
    return this.renderer;
  }

  // ---------- controls ----------

  /**
   * Attach OrbitControls to the camera + canvas. Returns the controls
   * instance so callers can read state (target, etc.) and so tests can
   * assert configuration.
   *
   * dampingFactor=0.05 gives that "smooth heavy model" feel without
   * feeling laggy on small adjustments.
   */
  enableOrbitControls(): OrbitControls {
    if (this.controls) {
      return this.controls;
    }
    const c = new OrbitControls(this.camera, this.canvas);
    c.enableDamping = true;
    c.dampingFactor = CONTROLS_DAMPING;
    c.enableZoom = true;
    c.enablePan = true;
    c.enableRotate = true;
    this.controls = c;
    return c;
  }

  // ---------- grid floor (T-1.1.4) ----------

  /**
   * Add a 16×16 grid floor to the scene (XZ plane, y=0). Subsequent calls
   * without an intervening removeGridFloor() are a no-op — only one grid
   * may exist at a time. Defaults satisfy PRD FR-101 (≥16×16, unit spacing).
   *
   * Why a single grid slot? Constitution §2.3 #8 — view filtering toggles
   * visibility without duplicating scene state. We keep the grid as a
   * single owned reference and add/remove it on demand.
   */
  addGridFloor(
    size: number = GRID_FLOOR_DEFAULT_SIZE,
    divisions: number = GRID_FLOOR_DEFAULT_DIVISIONS,
  ): void {
    if (this.gridFloor) {
      return;
    }
    this.gridFloor = createGridFloor(size, divisions);
    this.scene.add(this.gridFloor);
  }

  /**
   * Remove the grid floor from the scene, if any. Safe to call when no
   * grid is attached (no-op). Does not affect camera, controls, or any
   * other scene content.
   */
  removeGridFloor(): void {
    if (!this.gridFloor) {
      return;
    }
    this.scene.remove(this.gridFloor);
    this.gridFloor = null;
  }

  /**
   * Get the current grid floor instance, or null if none is attached.
   * Returned reference is owned by MainScene — do not mutate its transform
   * or material from outside.
   */
  getGridFloor(): GridHelper | null {
    return this.gridFloor;
  }

  // ---------- render loop ----------

  /**
   * Start the requestAnimationFrame loop. Re-entrant safe: starting twice
   * without an intervening stop is a no-op.
   */
  startRenderLoop(): void {
    if (this.rafHandle !== null || this.disposed) {
      return;
    }
    const tick = () => {
      if (this.disposed) {
        return;
      }
      if (this.controls) {
        this.controls.update();
      }
      this.renderer.render(this.scene, this.camera);
      this.rafHandle = requestAnimationFrame(tick);
    };
    this.rafHandle = requestAnimationFrame(tick);
  }

  /**
   * Cancel any pending frame. Safe to call when no loop is running.
   */
  stopRenderLoop(): void {
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
  }

  // ---------- teardown ----------

  // ---------- view reset (T-1.1.5) ----------

  /**
   * Reset the camera and OrbitControls target to the PRD FR-101 isometric
   * default: camera at (10, 10, 10) looking at the scene origin, controls
   * target re-anchored at (0, 0, 0). Safe to call before OrbitControls is
   * attached — the target reset is then applied on the next attach.
   *
   * Why this is separate from the constructor:
   *   - Construction-time defaults are a one-shot. After OrbitControls lets
   *     the user orbit/zoom/pan, those defaults are stale. This is the API
   *     surface the UI "重置视角" button calls.
   *   - We intentionally do not tween or animate — the existing dampening
   *     on OrbitControls + an instant snap-back is the simplest correct
   *     behaviour and matches PRD FR-101 (isometric default view).
   */
  resetView(): void {
    this.camera.position.set(CAMERA_POSITION[0], CAMERA_POSITION[1], CAMERA_POSITION[2]);
    this.camera.lookAt(SCENE_CENTER[0], SCENE_CENTER[1], SCENE_CENTER[2]);
    if (this.controls) {
      this.controls.target.set(SCENE_CENTER[0], SCENE_CENTER[1], SCENE_CENTER[2]);
      this.controls.update();
    }
  }

  // ---------- teardown ----------

  /**
   * Tear down renderer + controls + raf. Idempotent: second call is a no-op.
   * Must be called from React useEffect cleanup to avoid leaks across HMR
   * and unmounts.
   */
  dispose(): void {
    if (this.disposed) {
      return;
    }
    this.stopRenderLoop();
    if (this.controls) {
      this.controls.dispose();
      this.controls = null;
    }
    this.renderer.dispose();
    this.disposed = true;
  }
}
